import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GoogleMap, InfoWindow, Marker, Polyline } from "@react-google-maps/api";
import { FaBatteryQuarter, FaMapMarkedAlt, FaRoute, FaSyncAlt } from "react-icons/fa";
import { MdOutlineLocalShipping, MdOutlineAssignmentReturn, MdOutlineWarningAmber } from "react-icons/md";
import Handler from "../../Helpers/Handler";
import useGoogleMaps from "../../Helpers/useGoogleMaps";
import { Skeleton } from "../../Components/Needed";
import useListParams from "../../Helpers/useListParams";
import {
    STATUS_META, TASK_COLOR, MAP_OPTIONS, MAP_STYLE, PROBLEM_TEXT,
    agoText, fitTo, initials, metresText, pinIcon, toLatLng,
} from "./trackingShared";

const REFRESH_MS = 10000;
const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 };

const TaskIcon = ({ type }) => (String(type).toLowerCase() === "pickup" ? <MdOutlineAssignmentReturn /> : <MdOutlineLocalShipping />);

const StaffTracking = () => {
    const { isLoaded, loadError } = useGoogleMaps();
    const mapRef = useRef(null);
    const fittedFor = useRef(null);
    const [staff, setStaff] = useState([]);
    const [attention, setAttention] = useState([]);
    const [hubs, setHubs] = useState([]);
    const [hubId, setHubId] = useState("");
    // Search in the query string (?search=), so Back from a staff route keeps it - see Helpers/useListParams.
    const { filters: { search }, setFilter } = useListParams({ search: "" });
    const setSearch = (value) => setFilter("search", value);
    const [selectedId, setSelectedId] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [updatedAt, setUpdatedAt] = useState(null);
    const [auto, setAuto] = useState(true);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "GET", url: "/staff/hub/active" });
            setHubs(response?.success ? response?.data?.data || [] : []);
        })();
    }, []);

    const load = useCallback(async () => {
        const [live, quiet] = await Promise.all([
            Handler({ method: "GET", url: "/staff/location/live", params: hubId ? { hubId } : {} }),
            Handler({ method: "GET", url: "/staff/location/attention" }),
        ]);
        if (live?.success) {
            setStaff(live?.data?.data?.staff || []);
            setUpdatedAt(new Date());
            setError("");
        } else {
            setError(live?.data?.message || "Could not load staff locations");
        }
        setAttention(quiet?.success ? quiet?.data?.data || [] : []);
        setLoading(false);
    }, [hubId]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    // Refresh every 10 s while the tab is visible; "x seconds ago" labels tick along.
    useEffect(() => {
        const timer = setInterval(() => {
            setNow(Date.now());
            if (auto && !document.hidden) load();
        }, REFRESH_MS);
        return () => clearInterval(timer);
    }, [auto, load]);

    const counts = useMemo(() => ({
        total: staff.length,
        live: staff.filter((s) => s.status === "live").length,
        task: staff.filter((s) => s.task).length,
        quiet: staff.filter((s) => s.status === "offline").length,
    }), [staff]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return staff;
        return staff.filter((s) => [s.name, s.staffId, s.task?.taskId, s.task?.bookingId].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
    }, [staff, search]);

    const selected = staff.find((s) => s.staffId === selectedId) || null;

    // Fit the map to everyone once per hub choice; never on a refresh, so the view does not jump while an admin is looking around.
    useEffect(() => {
        if (!isLoaded || !mapRef.current || !staff.length) return;
        if (fittedFor.current === hubId) return;
        fittedFor.current = hubId;
        fitTo(mapRef.current, window.google, staff.map((s) => toLatLng(s.position)));
    }, [isLoaded, staff, hubId]);

    const focus = (item) => {
        setSelectedId(item.staffId);
        const point = toLatLng(item.position);
        if (point && mapRef.current) {
            mapRef.current.panTo(point);
            if ((mapRef.current.getZoom() || 0) < 15) mapRef.current.setZoom(15);
        }
    };

    const destination = selected?.task?.destination ? toLatLng(selected.task.destination) : null;

    return (
        <>
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <div className="me-auto">
                    <h4 className="fw-bold mb-0 d-flex align-items-center gap-2"><FaMapMarkedAlt /> Live Staff Map</h4>
                    <small className="text-muted">
                        {updatedAt ? `Updated ${updatedAt.toLocaleTimeString()}` : "Loading..."} · refreshes every 10 s
                    </small>
                </div>
                <select className="form-select w-auto" value={hubId} onChange={(e) => { setHubId(e.target.value); setSelectedId(""); }} aria-label="Hub">
                    <option value="">All hubs</option>
                    {hubs.map((h) => <option key={h.hubId} value={h.hubId}>{[h.buildingNo, h.area, h.city].filter(Boolean).join(", ") || h.hubId}</option>)}
                </select>
                <div className="form-check form-switch m-0">
                    <input className="form-check-input" type="checkbox" role="switch" id="stf-auto" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
                    <label className="form-check-label" htmlFor="stf-auto">Auto refresh</label>
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2" onClick={load}><FaSyncAlt /> Refresh</button>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="custom-badge badge-verified">{counts.total} on the map</span>
                <span className="custom-badge badge-active">{counts.live} live</span>
                <span className="custom-badge badge-warning">{counts.task} on a task</span>
                <span className="custom-badge badge-inactive">{counts.quiet} no signal</span>
            </div>

            {error ? <div className="alert alert-danger py-2">{error}</div> : null}

            {attention.length ? (
                <div className="alert alert-warning py-2 mb-3">
                    <div className="fw-semibold d-flex align-items-center gap-2 mb-1"><MdOutlineWarningAmber /> {attention.length} task{attention.length > 1 ? "s" : ""} need attention</div>
                    <div className="d-flex flex-column gap-1 small">
                        {attention.map((item) => (
                            <div key={item.taskId} className="d-flex flex-wrap gap-2 align-items-center">
                                <Link to={`/tasks/${item.taskId}`} className="fw-semibold">{item.taskType} {item.taskId}</Link>
                                <span>{item.staffName || item.staffId}: {PROBLEM_TEXT[item.problem] || item.problem}{item.lastSeenAgeSec ? ` (last seen ${Math.max(1, Math.round(item.lastSeenAgeSec / 60))} min ago)` : ""}</span>
                                <Link to={`/staff-tracking/route/${item.staffId}?taskId=${item.taskId}`} className="ms-auto">Route</Link>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="st-track">
                <div className="st-track-list card border-0 shadow-sm rounded-4">
                    <div className="p-3 pb-2">
                        <input type="search" className="form-control" placeholder="Search name, staff, task or booking" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <div className="st-track-scroll px-2 pb-2">
                        {loading ? (
                            <div className="p-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={64} className="mb-2 rounded-3" />)}</div>
                        ) : visible.length ? visible.map((item) => {
                            const meta = STATUS_META[item.status] || STATUS_META.offline;
                            return (
                                <button type="button" key={item.staffId} className={`st-track-item ${selectedId === item.staffId ? "active" : ""}`} onClick={() => focus(item)}>
                                    <span className="st-track-avatar" style={{ background: meta.color }}>{initials(item.name)}</span>
                                    <span className="flex-grow-1 min-w-0 text-start">
                                        <span className="d-flex align-items-center gap-2">
                                            <span className="fw-semibold text-truncate">{item.name}</span>
                                            <span className={`custom-badge ${meta.badge}`}>{meta.label}</span>
                                        </span>
                                        <span className="d-block small text-muted text-truncate">
                                            {item.task ? <><TaskIcon type={item.task.taskType} /> {item.task.taskType} {item.task.taskId}{item.task.arrivedAt ? " · arrived" : item.task.distanceToAddressM !== null ? ` · ${metresText(item.task.distanceToAddressM)} to address` : ""}</> : "On shift"}
                                        </span>
                                        <span className="d-block small text-muted">
                                            {agoText(item.position?.at, now)}
                                            {Number.isFinite(item.battery) && item.battery <= 20 ? <span className="text-danger ms-2"><FaBatteryQuarter /> {item.battery}%</span> : null}
                                        </span>
                                    </span>
                                </button>
                            );
                        }) : (
                            <p className="text-muted text-center p-4 mb-0">
                                {staff.length ? "No one matches your search." : "Nobody is sharing location right now. Staff appear here once they check in on the staff app."}
                            </p>
                        )}
                    </div>
                </div>

                <div className="st-track-map card border-0 shadow-sm rounded-4">
                    {loadError ? <div className="p-4 text-danger">The map could not be loaded. Check the Google Maps key.</div> : !isLoaded ? <Skeleton height="100%" /> : (
                        <GoogleMap mapContainerStyle={MAP_STYLE} center={DEFAULT_CENTER} zoom={11} options={MAP_OPTIONS} onLoad={(map) => { mapRef.current = map; }} onClick={() => setSelectedId("")}>
                            {visible.map((item) => {
                                const point = toLatLng(item.position);
                                if (!point) return null;
                                const meta = STATUS_META[item.status] || STATUS_META.offline;
                                return (
                                    <Marker
                                        key={item.staffId}
                                        position={point}
                                        title={item.name}
                                        label={{ text: initials(item.name), color: "#ffffff", fontSize: "11px", fontWeight: "700" }}
                                        icon={pinIcon(window.google, { color: meta.color, ring: !!item.task, scale: selectedId === item.staffId ? 16 : 13 })}
                                        zIndex={selectedId === item.staffId ? 10 : item.task ? 5 : 1}
                                        onClick={() => focus(item)}
                                    />
                                );
                            })}
                            {destination ? <Marker position={destination} title="Delivery / pick-up address" label={{ text: "A", color: "#ffffff", fontWeight: "700" }} /> : null}
                            {destination && selected && toLatLng(selected.position) ? (
                                <Polyline path={[toLatLng(selected.position), destination]} options={{ strokeColor: TASK_COLOR, strokeOpacity: 0.7, strokeWeight: 3, geodesic: true }} />
                            ) : null}
                            {selected && toLatLng(selected.position) ? (
                                <InfoWindow position={toLatLng(selected.position)} onCloseClick={() => setSelectedId("")} options={{ pixelOffset: new window.google.maps.Size(0, -18) }}>
                                    <div className="st-track-info">
                                        <div className="fw-bold">{selected.name}</div>
                                        <div className="small text-muted mb-1">{selected.staffId} · {agoText(selected.position?.at, now)}</div>
                                        {selected.task ? (
                                            <div className="small mb-1">
                                                <Link to={`/tasks/${selected.task.taskId}`}>{selected.task.taskType} {selected.task.taskId}</Link>
                                                {selected.task.arrivedAt ? " · arrived" : selected.task.distanceToAddressM !== null ? ` · ${metresText(selected.task.distanceToAddressM)} from address` : ""}
                                            </div>
                                        ) : <div className="small mb-1">On shift, no task</div>}
                                        {Number.isFinite(selected.position?.speed) ? <div className="small mb-1">Speed {Math.round(selected.position.speed * 3.6)} km/h</div> : null}
                                        <div className="d-flex gap-3 small mt-2">
                                            <Link to={`/staff-tracking/route/${selected.staffId}`} className="d-inline-flex align-items-center gap-1"><FaRoute /> Route today</Link>
                                            
                                        </div>
                                    </div>
                                </InfoWindow>
                            ) : null}
                        </GoogleMap>
                    )}
                </div>
            </div>
        </>
    );
};

export default StaffTracking;
